import { GLOBAL_SEARCH_ENTITY_ICON } from 'components/global-search/entityDisplay';
import { CheckIcon, ChevronRightIcon, MessageCircleQuestionMarkIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from 'components/ui/input-group';
import { FormEvent, useEffect, useEffectEvent, useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { ChooseOption, ChooseOptionsQuestion, ChooseOptionsSpec } from 'types/aiChat';
import { cn } from 'utils/cn';
import { ENTITY_TYPE_FAMILY, familyTileClass, NEUTRAL_TILE_CLASS } from 'utils/families';
import { isComposing } from 'utils/isComposing';
import { getInitials } from 'utils/nameOrder';
import { useAiChatOptionPick } from '../../aiChatOptionPickContext';
import { optionText } from '../../utils/chooseOptionsSpec';
import { FigureFrame } from './figure/FigureFrame';

interface OptionsToolPartProps {
    spec: ChooseOptionsSpec;
    /** Whether the message offering these choices is the thread's last one. */
    isLatest: boolean;
    /** The family pair of the records an untyped hinted option stands for; neutral when unknown. */
    recordTileClassName?: string;
}

/** A typed answer wins while it is not blank; emptying it falls back to the option picked before. */
interface Answer {
    optionIndex?: number;
    ownText: string;
}

/** What a single question's click sent: a listed option, or the text typed in its own row. */
type SentChoice = { optionIndex: number } | { ownText: string };

const NO_ANSWER: Answer = { ownText: '' };

function answerText(question: ChooseOptionsQuestion, { optionIndex, ownText }: Answer) {
    if (ownText.trim()) return ownText.trim();
    return optionIndex === undefined ? '' : optionText(question.options[optionIndex]);
}

/** `Topic: answer · Topic: answer`, the shape the system prompt tells the model to read several answers in. */
function composeAnswers(questions: ChooseOptionsQuestion[], answers: string[]) {
    return questions.map((question, index) => `${question.topic ?? question.question}: ${answers[index]}`).join(' · ');
}

const isRecordQuestion = (question: ChooseOptionsQuestion) => question.options.some((option) => option.hint);

function PickedCheck() {
    return (
        <span
            aria-hidden="true"
            className="bg-primary text-primary-foreground grid size-5 shrink-0 place-items-center rounded-full"
        >
            <CheckIcon className="size-3" />
        </span>
    );
}

interface ChoiceRowProps {
    option: ChooseOption;
    tileClassName: string;
    picked: boolean;
    dimmed: boolean;
    locked: boolean;
    /** The row sends on its click, so it ends with the open arrow while it can. */
    sends: boolean;
    onPick: () => void;
}

/**
 * One option on a line. A hinted option is a record: its tile, its name and the hint under it. A typed option's tile
 * is its record type's glyph in its family's colours, as its chip; otherwise the search's tile with the name's
 * initials. Locked rows stay focusable, so the one just clicked keeps the focus while its answer goes out.
 */
function ChoiceRow({ option, tileClassName, picked, dimmed, locked, sends, onPick }: ChoiceRowProps) {
    const TypeIcon = option.entityType && GLOBAL_SEARCH_ENTITY_ICON[option.entityType];
    return (
        <Button
            type="button"
            variant="outline"
            aria-pressed={picked}
            disabled={locked}
            focusableWhenDisabled
            onClick={onPick}
            className={cn(
                'group/row border-border bg-card hover:bg-row-stripe aria-disabled:hover:bg-card new:border-0 new:shadow-card new:outline-hidden h-auto w-full justify-start gap-3 rounded-[20px] py-2.5 pe-3 text-start font-normal whitespace-normal shadow-none aria-disabled:cursor-default',
                option.hint ? 'ps-2.5' : 'ps-4',
                // Picked, its edge is the accent: a ring, as the card's edge is its shadow's.
                picked &&
                    'border-primary bg-primary-wash aria-disabled:hover:bg-primary-wash new:ring-primary new:ring-1',
                dimmed && 'opacity-60'
            )}
        >
            {option.hint ? (
                <span
                    aria-hidden="true"
                    className={cn(
                        'text-meta grid size-9 shrink-0 place-items-center rounded-[10px] font-semibold',
                        option.entityType ? familyTileClass(ENTITY_TYPE_FAMILY[option.entityType]) : tileClassName
                    )}
                >
                    {TypeIcon ? (
                        <TypeIcon className="size-[18px]" />
                    ) : (
                        // A contact's label may lead with its party number, "[T002] Tallinna Loomaaed": the name's initials.
                        getInitials(option.label.replace(/^\[[^\]]*\]\s*/, ''))
                    )}
                </span>
            ) : null}
            <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-body1 text-foreground font-medium">{option.label}</span>{' '}
                {option.hint ? <span className="text-caption text-foreground-tertiary">{option.hint}</span> : null}
            </span>
            {picked ? (
                <PickedCheck />
            ) : sends && !locked ? (
                <ChevronRightIcon
                    aria-hidden="true"
                    className="text-foreground-tertiary size-4 shrink-0 opacity-70 transition-opacity duration-(--duration-quick) ease-out group-hover/row:opacity-100 rtl:-scale-x-100"
                />
            ) : null}
        </Button>
    );
}

interface OwnAnswerFieldProps {
    value: string;
    onChange: (value: string) => void;
    /** Names the field when several questions each have one. */
    label?: string;
    locked: boolean;
    picked: boolean;
    dimmed: boolean;
    /** Enter and the row's chevron send what is typed; without it the block's Send does. */
    onSend?: () => void;
}

/** The "Something else…" row: a text field in an option's card, its chevron sending what is typed. */
function OwnAnswerField({ value, onChange, label, locked, picked, dimmed, onSend }: OwnAnswerFieldProps) {
    const intl = useIntl();
    const somethingElse = intl.formatMessage({ id: 'ai-chat.options.something-else' });
    const typed = value.trim().length > 0;

    return (
        <InputGroup
            className={cn(
                'border-border bg-card has-[[data-slot=input-group-control]:focus-visible]:border-primary h-auto rounded-[20px] py-1.5 ps-4 pe-1 shadow-none transition-[color,border-color,background-color] duration-(--duration-quick) ease-out',
                picked && 'border-primary bg-primary-wash',
                dimmed && 'opacity-60'
            )}
        >
            <InputGroupInput
                aria-label={label ?? somethingElse}
                placeholder={somethingElse}
                value={value}
                readOnly={locked}
                aria-disabled={locked || undefined}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={(event) => {
                    if (onSend && event.key === 'Enter' && !isComposing(event)) {
                        event.preventDefault();
                        onSend();
                    }
                }}
                className="text-foreground placeholder:text-foreground-tertiary md:text-body1 h-auto p-0 py-1 text-base font-medium placeholder:font-normal"
            />
            {onSend && (picked || !locked) ? (
                <InputGroupAddon align="inline-end">
                    {picked ? (
                        <PickedCheck />
                    ) : (
                        <InputGroupButton
                            size="icon-sm"
                            aria-label={intl.formatMessage({ id: 'ai-chat.options.send' })}
                            disabled={!typed}
                            focusableWhenDisabled
                            onClick={onSend}
                            className={cn(
                                'rounded-full aria-disabled:cursor-default',
                                typed ? 'text-foreground hover:bg-muted' : 'text-foreground-tertiary opacity-70'
                            )}
                        >
                            <ChevronRightIcon className="size-4 rtl:-scale-x-100" />
                        </InputGroupButton>
                    )}
                </InputGroupAddon>
            ) : null}
        </InputGroup>
    );
}

/**
 * Every question the assistant asks before it can go on, drawn as a choice Figure: the question as its title and the
 * options one per line. The model re-resolves from the candidates it just listed, so nothing but the composed text
 * travels back.
 *
 * One question answers on the first click, as a plain new user message. Options without a hint end with a row of
 * their own for an answer none of them carries; under records the composer reads "Or type the name…" instead, since
 * a name is what the user would type. Several questions are answered together: a click selects that question's
 * answer, a typed one replaces it, and a single Send sends them all as one message once every question has an answer.
 *
 * The text is composed here rather than in the surfaces reading the pick context, so every surface sends the same.
 * There is no "none of these": the tool never authors one, because clicking it would send that literal string
 * instead of the answer, and typing is always the way out.
 *
 * Only the thread's last message offers live choices. Once anything follows it, a click would answer a question the
 * conversation has moved past, and the model would resolve it against whatever is being discussed now, so the block
 * stays readable but every control in it is disabled. Position is all it takes, so a reloaded thread renders the
 * same.
 */
export function OptionsToolPart({ spec, isLatest, recordTileClassName = NEUTRAL_TILE_CLASS }: OptionsToolPartProps) {
    const intl = useIntl();
    const { isDisabled } = useAiChatOptionPick();
    const disabled = isDisabled || !isLatest;
    const single = spec.questions.length === 1;

    return (
        <FigureFrame
            icon={MessageCircleQuestionMarkIcon}
            tileClassName={NEUTRAL_TILE_CLASS}
            title={single ? spec.questions[0].question : undefined}
            meta={intl.formatMessage({ id: 'ai-chat.options.pick-one' })}
        >
            {single ? (
                <SingleQuestion
                    question={spec.questions[0]}
                    isLatest={isLatest}
                    disabled={disabled}
                    tileClassName={recordTileClassName}
                />
            ) : (
                <QuestionBlock questions={spec.questions} disabled={disabled} tileClassName={recordTileClassName} />
            )}
        </FigureFrame>
    );
}

interface SingleQuestionProps {
    question: ChooseOptionsQuestion;
    isLatest: boolean;
    disabled: boolean;
    tileClassName: string;
}

function SingleQuestion({ question, isLatest, disabled, tileClassName }: SingleQuestionProps) {
    const intl = useIntl();
    const { onSelect, setPlaceholder } = useAiChatOptionPick();
    const [picked, setPicked] = useState<SentChoice | null>(null);
    const [ownText, setOwnText] = useState('');
    const locked = disabled || picked !== null;
    const records = isRecordQuestion(question);
    const typeName = intl.formatMessage({ id: 'ai-chat.options.type-name' });

    // While the newest record choice is unanswered, the composer says a name can be typed instead.
    const awaitsName = records && isLatest && picked === null;
    const showComposerPlaceholder = useEffectEvent((text: string | null) => setPlaceholder(text));
    useEffect(() => {
        if (!awaitsName) {
            return undefined;
        }
        showComposerPlaceholder(typeName);
        return () => showComposerPlaceholder(null);
    }, [awaitsName, typeName]);

    const pick = (choice: SentChoice, text: string) => {
        if (locked) return;
        setPicked(choice);
        onSelect(text);
    };
    const pickedIndex = picked && 'optionIndex' in picked ? picked.optionIndex : undefined;
    const sentOwn = picked !== null && 'ownText' in picked;

    return (
        <div className="border-divider-soft flex flex-col gap-2 border-t px-4 pt-3 pb-4">
            {question.options.map((option, index) => (
                <ChoiceRow
                    key={optionText(option)}
                    option={option}
                    tileClassName={tileClassName}
                    picked={pickedIndex === index}
                    dimmed={picked === null ? !isLatest : pickedIndex !== index}
                    locked={locked}
                    sends
                    onPick={() => pick({ optionIndex: index }, optionText(option))}
                />
            ))}
            {records ? null : (
                <OwnAnswerField
                    value={ownText}
                    onChange={setOwnText}
                    locked={locked}
                    picked={sentOwn}
                    dimmed={picked === null ? !isLatest : !sentOwn}
                    onSend={() => {
                        const text = ownText.trim();
                        if (text) pick({ ownText: text }, text);
                    }}
                />
            )}
        </div>
    );
}

interface QuestionBlockProps {
    questions: ChooseOptionsQuestion[];
    disabled: boolean;
    tileClassName: string;
}

function QuestionBlock({ questions, disabled, tileClassName }: QuestionBlockProps) {
    const intl = useIntl();
    const { onSelect } = useAiChatOptionPick();
    const [answers, setAnswers] = useState<Record<number, Answer>>({});
    const [sent, setSent] = useState(false);
    const locked = disabled || sent;

    const answerTexts = questions.map((question, index) => answerText(question, answers[index] ?? NO_ANSWER));
    const complete = answerTexts.every((text) => text.length > 0);

    function answer(questionIndex: number, change: Partial<Answer>) {
        setAnswers((current) => ({
            ...current,
            [questionIndex]: { ...NO_ANSWER, ...current[questionIndex], ...change },
        }));
    }

    function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (locked || !complete) return;
        setSent(true);
        onSelect(composeAnswers(questions, answerTexts));
    }

    return (
        <form onSubmit={handleSubmit} className="border-divider-soft flex flex-col gap-4 border-t px-4 pt-3 pb-4">
            {questions.map((question, questionIndex) => (
                <QuestionField
                    // The topics are unique within a block, which the parser enforces.
                    key={question.topic ?? questionIndex}
                    question={question}
                    answer={answers[questionIndex] ?? NO_ANSWER}
                    locked={locked}
                    tileClassName={tileClassName}
                    onAnswer={(value) => answer(questionIndex, value)}
                />
            ))}
            <Button
                type="submit"
                variant="outline"
                disabled={locked || !complete}
                focusableWhenDisabled
                className="text-meta h-8 self-start rounded-lg px-3 aria-disabled:cursor-default"
            >
                {intl.formatMessage({ id: 'ai-chat.options.send' })}
            </Button>
        </form>
    );
}

interface QuestionFieldProps {
    question: ChooseOptionsQuestion;
    answer: Answer;
    locked: boolean;
    tileClassName: string;
    onAnswer: (change: Partial<Answer>) => void;
}

function QuestionField({ question, answer, locked, tileClassName, onAnswer }: QuestionFieldProps) {
    const intl = useIntl();
    const questionId = useId();
    const somethingElse = intl.formatMessage({ id: 'ai-chat.options.something-else' });
    const typed = answer.ownText.trim().length > 0;

    return (
        <div role="group" aria-labelledby={questionId} className="flex flex-col gap-2">
            <p id={questionId} className="text-body1 text-foreground m-0 font-semibold">
                {question.question}
            </p>
            {question.options.map((option, index) => {
                const picked = !typed && answer.optionIndex === index;
                return (
                    <ChoiceRow
                        key={optionText(option)}
                        option={option}
                        tileClassName={tileClassName}
                        picked={picked}
                        dimmed={locked && !picked}
                        locked={locked}
                        sends={false}
                        onPick={() => onAnswer({ optionIndex: index, ownText: '' })}
                    />
                );
            })}
            <OwnAnswerField
                label={`${question.topic ?? question.question}: ${somethingElse}`}
                value={answer.ownText}
                onChange={(ownText) => onAnswer({ ownText })}
                locked={locked}
                picked={locked && typed}
                dimmed={locked && !typed}
            />
        </div>
    );
}
